import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  const effectiveDate = "January 29, 2026";
  const companyName = "Tickler";
  const companyEmail = "support@trytickler.com";
  const websiteUrl = "https://app.trytickler.com";
  const jurisdiction = "State of Delaware, United States";

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-8">
          {/* Header */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <Link to="/" className="text-orange-600 hover:text-orange-700 text-sm mb-4 inline-block">
              ← Back to App
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
            <p className="text-sm text-gray-600">Effective Date: {effectiveDate}</p>
            <p className="text-sm text-gray-600">Last Updated: {effectiveDate}</p>
          </div>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            {/* Introduction */}
            <section>
              <p className="mb-4">
                Welcome to {companyName}. These Terms of Service ("Terms", "Agreement") govern your access to and use of the {companyName} website, applications, and services (collectively, the "Service"). Please read these Terms carefully before using our Service.
              </p>
              <p className="mb-4">
                <strong>IMPORTANT:</strong> BY ACCESSING OR USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE TO THESE TERMS, YOU MAY NOT ACCESS OR USE THE SERVICE.
              </p>
            </section>

            {/* 1. Acceptance of Terms */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
              <p className="mb-3">By creating an account, accessing, or using {companyName}, you agree to be bound by:</p>
              <ul className="list-disc pl-6 space-y-2 mb-3">
                <li>These Terms of Service</li>
                <li>Our <Link to="/privacy" className="text-orange-600 hover:underline">Privacy Policy</Link></li>
                <li>Any additional terms and policies referenced herein or presented to you</li>
              </ul>
              <p>If you are using the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.</p>
            </section>

            {/* 2. Description of Service */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Description of Service</h2>
              <p className="mb-3">{companyName} is an investment idea tracking and organization platform that enables users to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Import and track investment ideas from social media platforms, including X (formerly Twitter)</li>
                <li>Organize, categorize, and manage investment research and ideas</li>
                <li>Receive customizable email digests summarizing tracked investments</li>
                <li>Create and participate in investment-focused groups</li>
                <li>Share investment ideas with other users</li>
                <li>Access financial data and research tools</li>
                <li>Create watchlists and polls related to investment topics</li>
              </ul>
              <p className="mb-4">
                <strong>DISCLAIMER:</strong> {companyName} is a research organization and information tracking tool ONLY. We do NOT provide investment advice, financial planning services, or recommendations to buy, sell, or hold any securities. All investment decisions are solely your responsibility.
              </p>
            </section>

            {/* 3. Eligibility */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Eligibility</h2>
              <p className="mb-3">To use {companyName}, you must:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Be at least 18 years of age or the age of legal majority in your jurisdiction</li>
                <li>Have the legal capacity to enter into a binding agreement</li>
                <li>Not be prohibited from using the Service under applicable laws</li>
                <li>Not have been previously suspended or removed from the Service</li>
              </ul>
            </section>

            {/* 4. Account Registration and Security */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Account Registration and Security</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">4.1 Account Creation</h3>
              <p className="mb-3">You may register for an account using third-party authentication services (such as X/Twitter). By doing so, you grant us permission to access certain information from those services as described in our Privacy Policy.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">4.2 Account Responsibilities</h3>
              <p className="mb-3">You are responsible for:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized access or security breach</li>
                <li>Ensuring your account information is accurate and up-to-date</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">4.3 Account Security</h3>
              <p>We implement reasonable security measures but cannot guarantee absolute security. You acknowledge that you provide information at your own risk.</p>
            </section>

            {/* 5. Third-Party Integrations */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Third-Party Integrations</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">5.1 X (Twitter) Integration</h3>
              <p className="mb-3">When you connect your X account to {companyName}:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>You grant us permission to access data that X makes available through its API, including but not limited to bookmarks, profile information, and other permitted data</li>
                <li>We will only read data from your account—we do not post, tweet, retweet, or modify your X account in any way</li>
                <li>You can revoke access at any time through your X account settings or within our application</li>
                <li>You acknowledge that X's Terms of Service also apply to your use of this integration</li>
                <li>We comply with X's Developer Agreement, Developer Policy, and all applicable platform rules</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">5.2 Financial Data Providers</h3>
              <p className="mb-3">We may display financial data from third-party providers. This data is provided "as is" and may be delayed or inaccurate. We are not responsible for the accuracy of third-party data.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">5.3 Payment Processing</h3>
              <p>Payment processing is handled by third-party payment processors (such as Stripe). Your use of payment services is subject to the respective processor's terms and privacy policies. We do not store your complete credit card information.</p>
            </section>

            {/* 6. User Content and Conduct */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. User Content and Conduct</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">6.1 Your Content</h3>
              <p className="mb-3">You retain ownership of content you create, upload, or share through the Service ("User Content"). By submitting User Content, you grant {companyName} a worldwide, non-exclusive, royalty-free license to use, copy, modify, and display such content solely for the purpose of operating and improving the Service.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">6.2 Content Standards</h3>
              <p className="mb-3">You agree not to submit content that:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Is unlawful, defamatory, obscene, or otherwise objectionable</li>
                <li>Infringes on intellectual property rights of others</li>
                <li>Contains malware, viruses, or harmful code</li>
                <li>Constitutes spam or unauthorized advertising</li>
                <li>Violates securities laws or promotes market manipulation</li>
                <li>Contains material non-public information (insider information)</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">6.3 Prohibited Conduct</h3>
              <p className="mb-3">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
                <li>Use automated systems (bots, scrapers) without our express permission</li>
                <li>Interfere with or disrupt the Service's infrastructure</li>
                <li>Circumvent any security or access controls</li>
                <li>Use the Service for any illegal purpose</li>
                <li>Impersonate others or misrepresent your affiliation</li>
                <li>Resell or redistribute the Service without authorization</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-2">6.4 Investment Content Guidelines</h3>
              <p className="mb-3">When tracking or sharing investment ideas through the Service, you agree that you will not:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Share information that you know or believe to be material non-public information about any company</li>
                <li>Post content intended to artificially influence the price of any security or mislead other users</li>
                <li>Present false or misleading information about any investment as fact</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-2">6.5 Sharing Ideas with Others</h3>
              <p className="mb-3">When you share investment ideas with other users or groups through the Service:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You or others sharing ideas may hold positions in the securities discussed and may buy or sell at any time</li>
                <li>Shared ideas are for informational purposes only and are not recommendations to buy, sell, or hold any security</li>
                <li>Recipients should perform their own research before making any investment decisions</li>
              </ul>
            </section>

            {/* 7. Investment Disclaimer */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Investment Disclaimer and Risk Acknowledgment</h2>
              <div className="mb-4">
                <p className="mb-3"><strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS.</strong></p>
              </div>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">7.1 No Investment Advice</h3>
              <p className="mb-3">{companyName} is NOT a registered investment advisor, broker-dealer, or financial planner. The Service does not provide:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Investment advice or recommendations</li>
                <li>Tax, legal, or accounting advice</li>
                <li>Personalized financial planning</li>
                <li>Securities analysis or research reports for compensation</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">7.2 Investment Risks</h3>
              <p className="mb-3">You acknowledge and agree that:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>All investments involve risk, including potential loss of principal</li>
                <li>Past performance does not guarantee future results</li>
                <li>You are solely responsible for your investment decisions</li>
                <li>You should consult qualified financial professionals before making investment decisions</li>
                <li>Information displayed in the Service may be incomplete, inaccurate, or outdated</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">7.3 Third-Party Content</h3>
              <p>Investment ideas, opinions, and content shared by other users or imported from social media platforms represent the views of those individuals only, not {companyName}. We do not endorse, verify, or guarantee the accuracy of any user-generated investment content.</p>
            </section>

            {/* 8. AI-Generated Content Disclaimer */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. AI-Generated Content Disclaimer</h2>
              <div className="mb-4">
                <p className="mb-3"><strong>IMPORTANT NOTICE REGARDING AI-GENERATED SUMMARIES AND ANALYSIS:</strong></p>
              </div>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">8.1 AI Limitations</h3>
              <p className="mb-3">{companyName} uses artificial intelligence (AI) to generate summaries, categorizations, and analysis of investment ideas. You acknowledge and agree that:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>AI IS NOT ALWAYS ACCURATE.</strong> AI-generated content may contain errors, inaccuracies, or "hallucinations" (fabricated information that appears plausible but is incorrect)</li>
                <li><strong>AI OUTPUT IS NOT INVESTMENT ADVICE.</strong> Nothing generated by AI in this Service should be viewed, construed, used, or considered as investment advice, financial guidance, or a recommendation to buy, sell, or hold any security</li>
                <li>AI summaries should NOT be relied upon as accurate representations of the original content</li>
                <li>AI may misinterpret, mischaracterize, or omit important information from source materials</li>
                <li>Company names, ticker symbols, financial figures, and other data extracted by AI may be incorrect</li>
                <li>AI-generated analysis reflects algorithmic processing, NOT professional financial analysis or human judgment</li>
                <li>You should always consult with qualified financial professionals before making any investment decisions</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">8.2 User Responsibility</h3>
              <p className="mb-3">You are solely responsible for:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Verifying any AI-generated content against original sources before making any decisions</li>
                <li>Not relying on AI summaries as a substitute for reading original investment research or filings</li>
                <li>Conducting your own due diligence on any investment ideas, regardless of AI analysis</li>
                <li>Understanding that AI-generated content is provided for convenience only and may be inaccurate</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">8.3 No Warranty of Accuracy</h3>
              <p>{companyName} makes NO WARRANTY, EXPRESS OR IMPLIED, regarding the accuracy, completeness, or reliability of any AI-generated content. We shall not be liable for any losses, damages, or decisions made based on AI-generated summaries or analysis.</p>
            </section>

            {/* 9. Intellectual Property */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Intellectual Property</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">8.1 Our Intellectual Property</h3>
              <p className="mb-3">The Service, including its original content, features, functionality, design, and branding (excluding User Content), is owned by {companyName} and protected by copyright, trademark, and other intellectual property laws.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">8.2 Limited License</h3>
              <p className="mb-3">We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial purposes in accordance with these Terms.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">8.3 Restrictions</h3>
              <p className="mb-3">You may not:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Copy, modify, or distribute the Service or its content</li>
                <li>Reverse engineer or decompile the Service</li>
                <li>Remove any copyright or proprietary notices</li>
                <li>Use the Service to develop competing products</li>
              </ul>
            </section>

            {/* 10. Privacy and Data Protection */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Privacy and Data Protection</h2>
              <p className="mb-3">Your privacy is important to us. Our collection, use, and protection of your personal information is governed by our <Link to="/privacy" className="text-orange-600 hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference.</p>
              <p>By using the Service, you consent to the collection and use of your information as described in the Privacy Policy.</p>
            </section>

            {/* 11. Fees and Payment */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Fees and Payment</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">10.1 Subscription Services</h3>
              <p className="mb-3">Certain features of the Service may require payment of fees. By subscribing to paid features, you agree to pay all applicable fees as described at the time of purchase.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">10.2 Billing</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Fees are billed in advance on a recurring basis (monthly or annually)</li>
                <li>You authorize us to charge your payment method for all fees incurred</li>
                <li>Prices are subject to change with reasonable notice</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">10.3 Refunds</h3>
              <p>Fees are generally non-refundable except as required by law or as explicitly stated in our refund policy. You may cancel your subscription at any time, but you will not receive a refund for the current billing period.</p>
            </section>

            {/* 12. Service Availability */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Service Availability and Modifications</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">11.1 Availability</h3>
              <p className="mb-3">We strive to maintain reliable service but do not guarantee uninterrupted availability. The Service may be temporarily unavailable due to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Scheduled maintenance and updates</li>
                <li>Unplanned outages or technical issues</li>
                <li>Third-party service disruptions</li>
                <li>Events beyond our reasonable control</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">11.2 Modifications</h3>
              <p>We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice. We will not be liable for any modification, suspension, or discontinuation of the Service.</p>
            </section>

            {/* 13. Termination */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Termination</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">12.1 Termination by You</h3>
              <p className="mb-3">You may terminate your account at any time through the account settings or by contacting us at {companyEmail}.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">12.2 Termination by Us</h3>
              <p className="mb-3">We may suspend or terminate your account immediately, without prior notice, if you:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Violate these Terms or any applicable laws</li>
                <li>Engage in fraudulent or abusive behavior</li>
                <li>Pose a security risk to the Service or other users</li>
                <li>Fail to pay applicable fees</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">12.3 Effect of Termination</h3>
              <p>Upon termination, your right to use the Service ceases immediately. We may delete your account data in accordance with our Privacy Policy. Provisions that by their nature should survive termination will survive.</p>
            </section>

            {/* 14. Disclaimers */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">14. Disclaimers</h2>
              <div className="mb-4">
                <p className="mb-3">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:</p>
                <ul className="list-disc pl-6 space-y-2 mb-3">
                  <li>IMPLIED WARRANTIES OF MERCHANTABILITY</li>
                  <li>FITNESS FOR A PARTICULAR PURPOSE</li>
                  <li>NON-INFRINGEMENT</li>
                  <li>ACCURACY, RELIABILITY, OR COMPLETENESS OF CONTENT</li>
                </ul>
                <p>WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.</p>
              </div>
            </section>

            {/* 15. Limitation of Liability */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">15. Limitation of Liability</h2>
              <div className="mb-4">
                <p className="mb-3">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
                <ul className="list-disc pl-6 space-y-2 mb-3">
                  <li>{companyName.toUpperCase()}, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                  <li>THIS INCLUDES, WITHOUT LIMITATION, DAMAGES FOR LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES</li>
                  <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER</li>
                </ul>
                <p>SOME JURISDICTIONS DO NOT ALLOW LIMITATIONS ON IMPLIED WARRANTIES OR EXCLUSION OF CERTAIN DAMAGES. IF THESE LAWS APPLY TO YOU, SOME OR ALL OF THE ABOVE LIMITATIONS MAY NOT APPLY.</p>
              </div>
            </section>

            {/* 16. Indemnification */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">16. Indemnification</h2>
              <p>You agree to indemnify, defend, and hold harmless {companyName}, its officers, directors, employees, agents, and affiliates from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Your User Content</li>
                <li>Your investment decisions or activities</li>
              </ul>
            </section>

            {/* 17. Dispute Resolution */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">17. Dispute Resolution</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">16.1 Informal Resolution</h3>
              <p className="mb-3">Before filing any formal dispute, you agree to contact us at {companyEmail} and attempt to resolve the dispute informally for at least 30 days.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">16.2 Arbitration Agreement</h3>
              <p className="mb-3">
                <strong>PLEASE READ CAREFULLY:</strong> You and {companyName} agree that any dispute, claim, or controversy arising out of or relating to these Terms or the Service shall be resolved by binding individual arbitration, rather than in court, except that either party may seek equitable relief in court for infringement of intellectual property rights.
              </p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">16.3 Class Action Waiver</h3>
              <p className="mb-3">YOU AND {companyName.toUpperCase()} AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">16.4 Arbitration Rules</h3>
              <p>Arbitration shall be conducted by a single arbitrator under the rules of the American Arbitration Association (AAA) or JAMS, at your election. The arbitration shall be held in {jurisdiction} or another mutually agreed location.</p>
            </section>

            {/* 18. Governing Law */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">18. Governing Law</h2>
              <p>These Terms shall be governed by and construed in accordance with the laws of the {jurisdiction}, without regard to its conflict of law provisions. Any legal action or proceeding not subject to arbitration shall be brought exclusively in the courts located in {jurisdiction}.</p>
            </section>

            {/* 19. Changes to Terms */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">19. Changes to Terms</h2>
              <p className="mb-3">We reserve the right to modify these Terms at any time. If we make material changes, we will notify you by:</p>
              <ul className="list-disc pl-6 space-y-2 mb-3">
                <li>Posting the updated Terms on our website</li>
                <li>Sending an email to your registered email address</li>
                <li>Displaying a prominent notice within the Service</li>
              </ul>
              <p>Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms. If you do not agree to the changes, you must stop using the Service.</p>
            </section>

            {/* 20. General Provisions */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">20. General Provisions</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">19.1 Entire Agreement</h3>
              <p className="mb-3">These Terms, together with the Privacy Policy, constitute the entire agreement between you and {companyName} regarding the Service.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">19.2 Severability</h3>
              <p className="mb-3">If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">19.3 Waiver</h3>
              <p className="mb-3">Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">19.4 Assignment</h3>
              <p className="mb-3">You may not assign or transfer these Terms without our prior written consent. We may assign our rights and obligations without restriction.</p>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">19.5 Notices</h3>
              <p>We may provide notices to you via email, posting on the Service, or other reasonable means. You may contact us at {companyEmail}.</p>
            </section>

            {/* 21. Contact Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">21. Contact Information</h2>
              <p className="mb-3">If you have any questions about these Terms, please contact us:</p>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p><strong>{companyName}</strong></p>
                <p>Email: <a href={`mailto:${companyEmail}`} className="text-orange-600 hover:underline">{companyEmail}</a></p>
                <p>Website: <a href={websiteUrl} className="text-orange-600 hover:underline">{websiteUrl}</a></p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                BY USING {companyName.toUpperCase()}, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO THESE TERMS, YOU ARE NOT AUTHORIZED TO USE THE SERVICE.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
